'use strict';

const Backbone = require('backbone');
const WidgetsView = require('../views/widgets.js');
const _ = require('lodash');
const paramUtils = require('../utils/params.js');
const Filters = require('../mixins/filters.js');
const Model = require('./model.js');
const object = require('../utils/object.js');

const setConfiguration = function (curation) {
    return curation.loadConfiguration().then(function (configuration) {
        if (configuration instanceof Backbone.Model) {
            curation.setConfiguration(configuration, {
                silent: true,
                navigate: false
            });
        } else { // We did not get a configuration back, let the filterHandler deal with it
            return configuration;
        }
    });
};

const missingConfigurationHandler = function (curation) {
    if (!curation.configuration) {
        return curation.buildEmptyConfigurationView();
    }
};

const Events = {};

const Curation = Model.mixin({
    beforeFilters: [],

    mixins: {
        filters: Filters
    },

    idAttribute: 'curation_id',

    constructor: function (router, definition, options) {
        const me = this;

        options = options || {};
        me.cid = _.uniqueId(me.cidPrefix);

        me.globalProps = {};
        me.configurationProps = _.create(me.globalProps, definition);
        const queryParams = me.parseParams(options.queryParams || {});
        me.queryProps = _.create(me.configurationProps, queryParams);
        me.attributes = _.create(me.queryProps, {});

        me.id = me.get(me.idAttribute);
        if (options.collection) { me.collection = options.collection; }
        me.changed = {};

        me.router = router;
        me.initialize.apply(me, arguments);
    },

    save: _.noop,

    getPath: function (overrides) {
        const me = this;
        return me.getBasePath(_.extend(object.ownProperties(me.queryProps), overrides));
    },

    getBasePath: function (props) {
        const me = this;
        return me.router.resolveCurationRoute(me.id, props);
    },

    getBaseUrlObject: function (props) {
        const me = this;
        return me.router.resolveCurationRouteObject(me.id, props);
    },

    /*
     * Route and Query string parameter parsing.
     * Default behavior parses all int looking things into ints
     */

    widgetsView: WidgetsView,

    widgetView: function (widget) {
        return widget.getView();
    },

    getChildView: function () {
        const me = this;
        let ChildView;

        const widgets = me.getWidgets();
        if (me.isFocus()) {
            ChildView = me._getWidgetsView();
        } else {
            ChildView = me._getWidgetView(widgets.first());
        }

        return ChildView;
    },

    _getWidgetsView: function () {
        const me = this;
        return me._resolveView(me.widgetsView);
    },

    _getWidgetView: function (widget) {
        const me = this;
        return me._resolveView(me.widgetView, widget);
    },

    _resolveView: function (view) {
        const me = this;

        if (_.isFunction(_.get(view, 'prototype.render'))) {
            return view;
        } else if (_.isFunction(view)) {
            return view.apply(me, Array.prototype.slice.call(arguments, 1));
        } else {
            throw new Error('view not defined');
        }
    },

    getChildViewAttributes: function () {
        const me = this;

        const widgets = me.getWidgets();
        if (me.isFocus()) {
            return {
                id: me.id,
                collection: widgets,
                dashboardModel: me
            };
        }

        const widget = widgets.first();

        return {
            id: widget.id,
            dashboardModel: widget
        };
    },

    loadConfiguration: function () {
        const me = this;
        return new Backbone.$.Deferred().resolve(me.buildConfiguration());
    },

    parseParams: paramUtils.parse,

    getQueryParams: function () {
        const me = this;
        return _.clone(me.queryParams) || {};
    },

    appendDefinition: function (child) {
        const me = this;
        const deferred = me.configuration.appendDefinition(child);

        // Trigger widget collection reset on definition change
        me.getWidgets();

        return deferred;
    },

    removeDefinition: function (child, options) {
        const me = this;
        const deferred = me.configuration.removeDefinition(child, options);
        // Trigger widget collection reset on definition change
        me.getWidgets();

        return deferred;
    },

    /*
     * Returns the view to show when the curation has no configuration
     */
    getEmptyConfigurationView: function () {
        const me = this;
        return me.emptyConfigurationView;
    },

    /*
     * Builds an instance of the empty curation view
     */
    buildEmptyConfigurationView: function () {
        const me = this;
        const View = me.getEmptyConfigurationView();

        return new View({
            model: me.buildConfiguration()
        });
    },

    /*
     * Returns true if not a details page
     */
    isFocus: function () {
        const me = this;

        const instanceId = me.getProp('instanceId');
        const widgetId = me.getProp('widgetId');
        return _.isUndefined(instanceId) && _.isUndefined(widgetId);
    },

    /*
     * Set the curation configuration and updating the curation properies
     */
    setConfiguration: function (configuration, options) {
        const me = this;

        me.resetPropHash('configurationProps', configuration.getProps(), options);
        me.configuration = configuration;
        me.configuration.resetWidgetCache();
    },

    /*
     * Reloads the curation and resets the widgets array
     */
    reloadConfiguration: function () {
        const me = this;
        me.loadConfiguration().then(function (configuration) {
            me.stopListening(me.configuration);
            me.setConfiguration(configuration);

            // To trigger reset event on the widgets collection
            me.getWidgets();
        });
    },

    requestAnimationFrame: function (fn) {
        const me = this;
        window.requestAnimationFrame(function () {
            if (!me.isTornDown) {
                fn.call(me);
            }
        });
    },

    /*
     * Cleanup code called before transitioning to another curation
     */
    teardown: function () {
        const me = this;
        me.isTornDown = true;
        Events.stopListeningToOthers.call(me);
    }
});

Events.stopListeningToOthers = function () {
    const me = this;

    const listeningTo = me._listeningTo;
    if (!listeningTo) {
        return me;
    }

    const list = _.values(listeningTo);
    _.each(list, function (listening) {
        if (listening.obj !== me) {
            listening.obj.off(null, null, me);
        }
    });

    if (_.isEmpty(listeningTo)) {
        me._listeningTo = undefined;
    }

    return me;
};

Curation.filters = {
    setConfiguration: setConfiguration,
    missingConfigurationHandler: missingConfigurationHandler
};

module.exports = Curation;
